import { HelpCircle } from "lucide-react";

import {
  NonFousableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/tooltip";

import { useRequestStatistics } from "../hooks";

interface RequestStatisticsProps {
  onTabChange?: (tab: string) => void;
}

export function RequestStatistics({ onTabChange }: RequestStatisticsProps) {
  const { data: statistics } = useRequestStatistics();

  const requestCounts = statistics?.counts;
  const activeSlots = statistics?.activeSlots;

  // Build tooltip content with only accessible request types
  const buildTooltipContent = () => {
    const parts = [];

    if (activeSlots?.graphic) {
      parts.push(`${activeSlots.graphic} graphic`);
    }

    if (activeSlots?.video) {
      parts.push(`${activeSlots.video} video`);
    }

    if (activeSlots?.website) {
      parts.push(`${activeSlots.website} website`);
    }

    return parts.length > 0 ? parts.join(", ") : "No active requests";
  };

  return (
    <div className="flex items-stretch text-sm divide-x divide-border-secondary w-full lg:w-auto lg:mr-4">
      <div className="flex flex-1 flex-col items-center lg:items-start px-3 py-3 lg:px-6 lg:py-4 lg:border-l border-border-secondary">
        <div className="flex items-center gap-1">
          <span className="text-text-tertiary text-xs lg:text-sm">
            Design slots
          </span>
          <TooltipTrigger delay={250}>
            <NonFousableTooltipTarget>
              <HelpCircle className="size-3 text-text-tertiary hover:text-text-secondary" />
            </NonFousableTooltipTarget>
            <Tooltip>
              The number of design requests being worked on simultaneously
            </Tooltip>
          </TooltipTrigger>
        </div>
        <TooltipTrigger delay={250}>
          <NonFousableTooltipTarget>
            <button
              className="font-semibold text-text-warning-primary text-xl lg:text-2xl hover:text-orange-600 cursor-pointer focus:outline-none focus:underline"
              onClick={() => onTabChange?.("active")}
              type="button"
            >
              {activeSlots?.activeRequests ?? 0}/
              {activeSlots?.maxTotalByCredits ?? 6}
            </button>
          </NonFousableTooltipTarget>
          <Tooltip>{buildTooltipContent()}</Tooltip>
        </TooltipTrigger>
      </div>

      <div className="flex flex-1 flex-col items-center lg:items-start px-3 py-3 lg:px-6 lg:py-4">
        <span className="text-text-tertiary text-xs lg:text-sm">Queued</span>
        <button
          className="font-semibold text-text-primary text-xl lg:text-2xl hover:text-text-secondary cursor-pointer focus:outline-none focus:underline"
          onClick={() => onTabChange?.("queued")}
          type="button"
        >
          {requestCounts?.queued || 0}
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center lg:items-start px-3 py-3 lg:px-6 lg:py-3">
        <span className="text-text-tertiary text-xs lg:text-sm">Completed</span>
        <button
          className="font-semibold text-text-success-primary text-xl lg:text-2xl hover:text-green-700 cursor-pointer focus:outline-none focus:underline"
          onClick={() => onTabChange?.("completed")}
          type="button"
        >
          {requestCounts?.completed || 0}
        </button>
      </div>
    </div>
  );
}
